import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { Routes, Route, Navigate, useParams, useSearchParams } from 'react-router'
import {
  useAuth,
  useProfileSync,
  RequireRole,
  LoginPage,
  UnauthorizedPage,
} from '@structural-climbing/auth'
import { FullPageSpinner } from '@structural-climbing/ui'
import { ProfileCompletionProvider } from './context/ProfileCompletionContext'
import { ViewedGymProvider } from './context/ViewedGymContext'
import { SeenNewsProvider } from './context/SeenNewsContext'
import { RootLayout } from './layouts/RootLayout'
import { GymLayout } from './layouts/GymLayout'
import { DashboardPage } from './pages/DashboardPage'
import { ProfilePage } from './pages/ProfilePage'
import { RoutesPage } from './pages/RoutesPage'
import { ProgressPage } from './pages/ProgressPage'
import { NewsPage } from './pages/NewsPage'
import { ClassesPage } from './pages/ClassesPage'
import { EventsPage } from './pages/EventsPage'
import { CompetitionsPage } from './pages/CompetitionsPage'
import { CompetitionPage } from './pages/CompetitionPage'
import { OurGymsPage } from './pages/OurGymsPage'
import { JoinPage } from './pages/JoinPage'
import { GuestJoinPage } from './pages/GuestJoinPage'
import { ChildInvitePage } from './pages/ChildInvitePage'
import { ChildProfilePage } from './pages/ChildProfilePage'
import { GymDetailsPage } from './pages/GymDetailsPage'
import { IdeasPage } from './pages/IdeasPage'
import { useMyMemberships } from './features/gym/useMyMemberships'
import { NotForChildAccount } from './features/family/NotForChildAccount'
import { FamilyPage } from './pages/FamilyPage'
import { GymRedirect } from './pages/GymRedirect'

// A bookmarked /:gymSlug/competitions/:id still resolves: the server accepts an
// id as readily as a slug, so the param travels to the global route unchanged.
function LegacyCompetitionRedirect() {
  const { competitionId } = useParams<{ competitionId: string }>()
  return <Navigate to={`/competitions/${competitionId ?? ''}`} replace />
}

/**
 * Routes and standings used to be one page with a Bouldering/Lead toggle in
 * `?discipline=`. They are one page per discipline now; an old link, bookmark
 * or installed-app history entry lands on the tab it was pointing at.
 */
export function LegacyDisciplineRedirect({ page }: { page: 'routes' | 'standings' }) {
  const [params] = useSearchParams()
  const discipline = params.get('discipline') === 'lead' ? 'lead' : 'bouldering'
  params.delete('discipline')
  const rest = params.toString()
  return <Navigate to={`../${discipline}/${page}${rest ? `?${rest}` : ''}`} replace />
}

/**
 * A child's own login gets a profile it can read and not edit; everyone else
 * gets the profile page as it has always been. Decided here so the two never
 * share state, and held back until the answer is known so a child never sees
 * the editable form flash past.
 */
function ProfileRoute({ section }: { section?: 'details' }) {
  const { childAccount, loading } = useMyMemberships()
  if (loading) return <FullPageSpinner />
  if (childAccount) return section ? <Navigate to="/profile" replace /> : <ChildProfilePage />
  return <ProfilePage section={section} />
}

/**
 * What a member sees when the app has genuinely broken.
 *
 * It used to be a bare unstyled <p>, which reads like the page failed to load
 * rather than like something went wrong and there is a way out of it.
 */
function CrashScreen() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-[var(--gym-page)] px-6 text-center">
      <span aria-hidden className="text-3xl">
        🧗
      </span>
      <p className="text-base font-semibold">Something went wrong.</p>
      <p className="max-w-xs text-sm text-foreground/50">
        The app hit a problem it could not recover from.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="mt-2 rounded-full bg-[var(--gym-primary)] px-5 py-2 text-sm font-medium text-[var(--gym-on-primary)]"
      >
        Reload
      </button>
    </div>
  )
}

export function App() {
  const { isLoading, user } = useAuth()
  useProfileSync()

  useEffect(() => {
    if (user) Sentry.setUser({ id: user.sub, email: user.email })
    else Sentry.setUser(null)
  }, [user])

  if (isLoading) return <FullPageSpinner />

  return (
    <Sentry.ErrorBoundary fallback={<CrashScreen />}>
      <ProfileCompletionProvider>
        <ViewedGymProvider>
          <SeenNewsProvider>
            <Routes>
              <Route path="/" element={<RootLayout />}>
                <Route
                  index
                  element={
                    <RequireRole role={['member', 'gym_owner', 'gym_staff']}>
                      <GymRedirect />
                    </RequireRole>
                  }
                />
                <Route
                  path="profile"
                  element={
                    <RequireRole role={['member', 'gym_owner', 'gym_staff']}>
                      <ProfileRoute />
                    </RequireRole>
                  }
                />
                <Route
                  path="profile/details"
                  element={
                    <RequireRole role={['member', 'gym_owner', 'gym_staff']}>
                      <ProfileRoute section="details" />
                    </RequireRole>
                  }
                />
                <Route
                  path="our-gyms"
                  element={
                    <RequireRole role={['member', 'gym_owner', 'gym_staff']}>
                      <NotForChildAccount>
                        <OurGymsPage />
                      </NotForChildAccount>
                    </RequireRole>
                  }
                />
                <Route
                  path="competitions"
                  element={
                    <RequireRole role={['member', 'gym_owner', 'gym_staff']}>
                      <CompetitionsPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="competitions/:competitionId"
                  element={
                    <RequireRole role={['member', 'gym_owner', 'gym_staff']}>
                      <CompetitionPage />
                    </RequireRole>
                  }
                />
                <Route
                  path="join/:gymSlug"
                  element={
                    <NotForChildAccount>
                      <JoinPage />
                    </NotForChildAccount>
                  }
                />
                <Route path="guest/:gymSlug" element={<GuestJoinPage />} />
                <Route path="child-invite" element={<ChildInvitePage />} />
                <Route path="login" element={<LoginPage />} />
                <Route path="unauthorized" element={<UnauthorizedPage portalName="Member App" />} />
                <Route
                  path=":gymSlug"
                  element={
                    <RequireRole role={['member', 'gym_owner', 'gym_staff']}>
                      <GymLayout />
                    </RequireRole>
                  }
                >
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="gym" element={<GymDetailsPage />} />
                  <Route path="ideas" element={<IdeasPage />} />
                  {/* Keyed by discipline so switching pills remounts the page
                      and no filter or scroll state leaks across scales. */}
                  <Route
                    path="bouldering/routes"
                    element={<RoutesPage key="boulder" discipline="boulder" />}
                  />
                  <Route path="lead/routes" element={<RoutesPage key="lead" discipline="lead" />} />
                  <Route
                    path="bouldering/standings"
                    element={<RoutesPage key="boulder" discipline="boulder" standingsOpen />}
                  />
                  <Route
                    path="lead/standings"
                    element={<RoutesPage key="lead" discipline="lead" standingsOpen />}
                  />
                  <Route path="bouldering" element={<Navigate to="routes" replace />} />
                  <Route path="lead" element={<Navigate to="routes" replace />} />
                  <Route path="routes" element={<LegacyDisciplineRedirect page="routes" />} />
                  <Route path="standings" element={<LegacyDisciplineRedirect page="standings" />} />
                  <Route path="progress" element={<ProgressPage />} />
                  <Route path="competitions" element={<Navigate to="/competitions" replace />} />
                  <Route
                    path="competitions/:competitionId"
                    element={<LegacyCompetitionRedirect />}
                  />
                  <Route path="profile" element={<Navigate to="/profile" replace />} />
                  <Route path="switch-gym" element={<Navigate to="/our-gyms" replace />} />
                  <Route path="classes" element={<ClassesPage />} />
                  {/* A child's own login never manages a family. */}
                  <Route
                    path="family"
                    element={
                      <NotForChildAccount>
                        <FamilyPage />
                      </NotForChildAccount>
                    }
                  />
                  <Route path="events" element={<EventsPage />} />
                  <Route path="news" element={<NewsPage />} />
                </Route>
              </Route>
            </Routes>
          </SeenNewsProvider>
        </ViewedGymProvider>
      </ProfileCompletionProvider>
    </Sentry.ErrorBoundary>
  )
}
